import {
  assign,
  concat,
  find,
  identity,
  isEmpty,
  isEqual,
  isNil,
  isNumber,
  isUndefined,
  omitBy,
  pickBy,
  reduce,
  toNumber,
  values,
} from 'lodash';

import { SplitInfoFormValues } from './SplitInfo';
import {
  AnticipatedScheduleTypeEnum,
  AssignmentAnticipatedScheduleEditModel,
  AssignmentBillingContactEditModel,
  AssignmentContactPersonEditModel,
  AssignmentContractorCompanyInfoEditModel,
  AssignmentFinancialSchedulerEditModel,
  AssignmentInfoEditModel,
  AssignmentMultiRateEditModel,
  AssignmentMultiRateViewModel,
  AssignmentPartnerDraftModel,
  AssignmentSplitInfoEditModel,
  AssignmentSupervisorInfoEditModel,
  AssignmentTeammatesEditModel,
  AssignmentTeammateSelectModel,
  AssignmentTeammateTypeEnum,
  AssignmentTimesheetApproverInfoEditModel,
  AssignmentTimesheetBackupApproverInfoEditModel,
  AssignmentTypeEnum,
  AssignmentWorkerInfoEditModel,
  LocationModel,
  MultiRateSchedulerEditModel,
  MultiRateSchedulerViewModel,
  OvertimeEnum,
  SplitTypeEnum,
  TimePointEnum,
  WorkerTypeEnum,
} from 'api/backofficeApiClient';
import {
  CustomerInfoFormValues,
  isPrimaryContactSectionRequired,
} from './CustomerInfo';
import { isAddWorkerSelected, WorkerFormValues } from './WorkerSection';
import { AssignmentInfoFormValues } from './AssignmentInfo';
import { WorksiteLocationFormValues } from './WorksiteLocation';
import { ContactInfoFormValues, isOtherOptionSelected } from './ContactInfo';
import { isAddCompanySelected } from './CompanySection';
import { getDateWithoutTime } from 'utils/dateUtils';
import { WorkerInfoFormValues } from './SCAssignments/WorkerInfo';
import { PartnerSplitEnum } from 'utils/assignmentUtils';
import {
  FinancialInfoSchedulerModel,
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
  PayCodeInfoFormScheduleValues,
} from '../FinancialInfoScheduleModal';
import { AssignmentInitialValues } from './AssignmentModal';
import {
  FinancialInfoFormValues,
  MultiRateFormValues,
  PayCodeInfoFormValues,
  WorkSiteWorkLocation,
} from './FinancialInfo';
import { BillingContactFormValues } from './CustomerInfo/BillingContactSection';
import { ExtraItemEnum } from 'enums';
import { AssignmentCreationTypeEnum } from './enum';
import { isEditorContentEmpty } from 'utils/editorUtils';
import { convertArraysToOneLength } from 'utils/arrayUtils';
import { countryUSAid } from 'constants/index';

interface SplitInfoRequest {
  splitInfo?: AssignmentSplitInfoEditModel;
  partnerDraft?: AssignmentPartnerDraftModel;
}

export const mapSplitFormValuesToRequest = (
  values: SplitInfoFormValues,
): SplitInfoRequest => {
  const { splitType, existingPartnerData, newPartnerData } = values;

  let result: SplitInfoRequest = {};

  switch (splitType) {
    case SplitTypeEnum.ExistingPartner:
      result = {
        splitInfo: {
          splitType,
          partnerId: existingPartnerData?.partner,
          // Pass splitAmount if PaymentPerHour is selected
          // otherwise - pass splitPercent
          splitAmount:
            existingPartnerData?.splitLabel ===
              PartnerSplitEnum.PaymentPerHour &&
            !isNil(existingPartnerData?.splitAmount)
              ? existingPartnerData?.splitAmount
              : undefined,
          splitPercent:
            existingPartnerData?.splitLabel === PartnerSplitEnum.SplitPercent &&
            !isNil(existingPartnerData?.splitAmount)
              ? existingPartnerData?.splitAmount
              : undefined,
        },
      };
      break;

    case SplitTypeEnum.NewPartner:
      result = {
        splitInfo: {
          splitType,
          // Pass splitAmount if PaymentPerHour is selected
          // otherwise - pass splitPercent
          splitAmount:
            newPartnerData?.splitLabel === PartnerSplitEnum.PaymentPerHour &&
            !isNil(newPartnerData?.splitAmount)
              ? newPartnerData?.splitAmount
              : undefined,
          splitPercent:
            newPartnerData?.splitLabel === PartnerSplitEnum.SplitPercent &&
            !isNil(newPartnerData?.splitAmount)
              ? newPartnerData?.splitAmount
              : undefined,
        },
        partnerDraft: {
          companyName: newPartnerData?.companyName.trim(),
          subcontractorRate: !isNil(newPartnerData?.subcontractorRate)
            ? newPartnerData?.subcontractorRate
            : undefined,
          countryId: newPartnerData?.country,
          stateId: toNumber(newPartnerData?.state),
          internationalStateName:
            newPartnerData?.internationalStateName || undefined,
          internationalCityName:
            newPartnerData?.internationalCityName || undefined,
          city: newPartnerData?.city,
          address: newPartnerData?.address,
          zipCode: newPartnerData?.zip || undefined,
          firstName: newPartnerData?.firstName,
          lastName: newPartnerData?.lastName,
          email: newPartnerData?.email,
          phoneNumber: newPartnerData?.phone,
        },
      };
      break;
    case SplitTypeEnum.NoSplit:
      result = {
        splitInfo: {
          splitType,
        },
      };
      break;

    default:
      break;
  }

  return result;
};

export const mapAssignmentInfoToRequest = (
  values: AssignmentInfoFormValues,
): AssignmentInfoEditModel => {
  const {
    startDate,
    endDate,
    jobTitle,
    jobDescription,
    jobCategory,
    employmentType,
    anticipateSchedule,
    anticipateOtherData,
    preScreeningRequired,
    preScreeningBilledTo,
    workFromHome,
    specialInstructions,
    supportingDocuments,
    // workCode,
  } = values;

  let anticipatedSchedule: AssignmentAnticipatedScheduleEditModel | undefined;

  if (anticipateSchedule === AnticipatedScheduleTypeEnum.Other) {
    anticipatedSchedule = {
      fromTime: anticipateOtherData?.hoursFrom as TimePointEnum,
      fromAMPM: anticipateOtherData?.dayTimeFrom,
      toTime: anticipateOtherData?.hoursTo as TimePointEnum,
      toAMPM: anticipateOtherData?.dayTimeTo,
    };
  }

  return {
    jobCategory: jobCategory || undefined,
    employmentType: employmentType || undefined,
    jobTitle,
    jobDescription: !isEditorContentEmpty(jobDescription.content)
      ? jobDescription.content
      : undefined,
    startDate: startDate ? getDateWithoutTime(startDate) : undefined,
    endDate: endDate ? getDateWithoutTime(endDate) : undefined,
    anticipatedScheduleType: isNumber(anticipateSchedule)
      ? anticipateSchedule
      : undefined,
    anticipatedSchedule,
    workFromHome,
    preScreeningRequired,
    preScreeningBilledTo: preScreeningBilledTo || undefined,
    specialInstructions: specialInstructions || undefined,
    attachments:
      supportingDocuments.length > 0
        ? supportingDocuments.map((document) => ({
            fileName: document.fileName,
            fileKey: document.s3Key,
          }))
        : undefined,
    // workCode: workCode?.trim() || undefined,
  };
};

interface CustomerInfoRequest {
  bestPrimaryContacts: AssignmentContactPersonEditModel[];
  supervisorInfo: AssignmentSupervisorInfoEditModel;
  timesheetApproverNotApplicable?: boolean;
  timesheetApproverSameAsSupervisor?: boolean;
  customerId?: string;
  timesheetApproverInfo?: AssignmentTimesheetApproverInfoEditModel;
  timesheetBackupApproverInfo: AssignmentTimesheetBackupApproverInfoEditModel;
  additionalTimesheetApprovers?: AssignmentTimesheetApproverInfoEditModel[];
  billingContacts: AssignmentBillingContactEditModel[];
}

export const mapCustomerInfoFormToRequest = (
  values: CustomerInfoFormValues,
  assignmentCreationType: AssignmentCreationTypeEnum,
): CustomerInfoRequest => {
  const {
    generalInfo: {
      customer,
      bestContacts: bestContactIds,
      primaryContacts,
      billingContacts: billingContactIds,
      newBillingContacts,
    },
    supervisorInfo,
    timesheetApproverInfo,
    timeBackupApprover,
  } = values;

  const mappedNewBillingContacts = newBillingContacts.map((billingContact) => ({
    ...billingContact,
    contactId: undefined,
  }));

  const mappedBillingContacts: BillingContactFormValues[] = billingContactIds
    .filter(
      (billingContact) => billingContact !== ExtraItemEnum.AddBillingContact,
    )
    .map((billingContact) => {
      return {
        contactId: billingContact || undefined,
      };
    })
    .concat(mappedNewBillingContacts);

  const bestPrimaryContacts: AssignmentContactPersonEditModel[] = bestContactIds
    .filter((contactId) => !isOtherOptionSelected(contactId))
    .map((contactId) => ({
      contactId,
    }));

  if (isPrimaryContactSectionRequired(bestContactIds)) {
    bestPrimaryContacts.push({
      firstName: primaryContacts?.firstName || undefined,
      lastName: primaryContacts?.lastName || undefined,
      email: primaryContacts?.email || undefined,
      title: primaryContacts?.title || undefined,
      phoneNumber: primaryContacts?.phone || undefined,
    });
  }

  let timesheetApprover;
  let extraTimesheetApprovers: AssignmentTimesheetApproverInfoEditModel[] = [];

  if (
    !timesheetApproverInfo?.notApplicable &&
    !timesheetApproverInfo?.sameAsSupervisor
  ) {
    const [firstTimesheetApprover] = timesheetApproverInfo.timesheetApprovers;

    timesheetApprover = {
      firstName: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.firstName
        : firstTimesheetApprover.firstName || undefined,
      lastName: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.lastName
        : firstTimesheetApprover.lastName || undefined,
      email: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.email
        : firstTimesheetApprover.email || undefined,
      phoneNumber: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo?.phone
        : firstTimesheetApprover?.phone || undefined,
    };
  }

  if (
    !timesheetApproverInfo?.notApplicable &&
    !!timesheetApproverInfo.timesheetApprovers.length
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [firstTimesheetApprover, ...restTimesheetApprovers] =
      timesheetApproverInfo.timesheetApprovers;

    extraTimesheetApprovers = restTimesheetApprovers.map(
      (timesheetApprover) => ({
        firstName: timesheetApprover?.firstName || undefined,
        lastName: timesheetApprover?.lastName || undefined,
        email: timesheetApprover?.email || undefined,
        phoneNumber: timesheetApprover?.phone || undefined,
      }),
    );
  }

  const data: CustomerInfoRequest = {
    bestPrimaryContacts,
    billingContacts: mappedBillingContacts,
    timesheetApproverNotApplicable:
      timesheetApproverInfo?.notApplicable || false,
    timesheetApproverSameAsSupervisor: timesheetApproverInfo.sameAsSupervisor,
    supervisorInfo: {
      firstName: supervisorInfo.firstName,
      lastName: supervisorInfo.lastName,
      email: supervisorInfo.email,
      phoneNumber: supervisorInfo.phone,
      title: supervisorInfo.title.trim() || undefined,
    },
    timesheetApproverInfo: timesheetApprover,
    additionalTimesheetApprovers: extraTimesheetApprovers,
    timesheetBackupApproverInfo: {
      firstName: timeBackupApprover.firstName,
      lastName: timeBackupApprover.lastName,
      email: timeBackupApprover.email,
      phoneNumber: timeBackupApprover.phone || undefined,
    },
  };

  if (assignmentCreationType !== AssignmentCreationTypeEnum.Renew) {
    data.customerId = customer;
  }

  return data;
};

interface ContactRequest {
  bestPrimaryContacts: AssignmentContactPersonEditModel[];
  supervisorInfo: AssignmentSupervisorInfoEditModel;
  timesheetApproverSameAsSupervisor: boolean;
  timesheetApproverInfo?: AssignmentTimesheetApproverInfoEditModel;
  timesheetBackupApproverInfo: AssignmentTimesheetBackupApproverInfoEditModel;
  timesheetApproverNotApplicable?: boolean;
  customerId?: string;
}

export const mapContactInfoToRequest = (
  values: ContactInfoFormValues,
): ContactRequest => {
  const {
    primaryContacts,
    supervisorInfo,
    timeBackupApprover,
    timesheetApprover,
  } = values;
  const { bestContacts, newPrimaryContact } = primaryContacts;
  const { supervisor, newSupervisor } = supervisorInfo;
  const { sameAsSupervisor, approver, newTimesheetApprover, notApplicable } =
    timesheetApprover;
  const bestPrimaryContacts: AssignmentContactPersonEditModel[] = bestContacts
    .filter((value) => !isOtherOptionSelected(value))
    .map((contactId) => ({
      contactId,
    }));

  const isOtherPrimaryContactSelected = bestContacts.some((value) =>
    isOtherOptionSelected(value),
  );

  if (isOtherPrimaryContactSelected) {
    bestPrimaryContacts.push({
      firstName: newPrimaryContact?.firstName || undefined,
      lastName: newPrimaryContact?.lastName || undefined,
      email: newPrimaryContact?.email || undefined,
      title: newPrimaryContact?.title || undefined,
      phoneNumber: newPrimaryContact?.phone || undefined,
    });
  }

  const supervisorInfoResult: AssignmentSupervisorInfoEditModel =
    isOtherOptionSelected(supervisor)
      ? {
          firstName: newSupervisor?.firstName,
          lastName: newSupervisor?.lastName,
          email: newSupervisor?.email,
          title: newSupervisor?.title,
          phoneNumber: newSupervisor?.phone,
        }
      : { supervisorId: supervisor };

  let timesheetApproverInfo:
    | AssignmentTimesheetApproverInfoEditModel
    | undefined;

  if (!sameAsSupervisor && !notApplicable) {
    if (isOtherOptionSelected(approver)) {
      timesheetApproverInfo = {
        firstName: newTimesheetApprover?.firstName || undefined,
        lastName: newTimesheetApprover?.lastName || undefined,
        email: newTimesheetApprover?.email || undefined,
        phoneNumber: newTimesheetApprover?.phone || undefined,
      };
    } else {
      timesheetApproverInfo = {
        timesheetApproverId: approver,
      };
    }
  }

  return {
    bestPrimaryContacts,
    supervisorInfo: supervisorInfoResult,
    timesheetApproverSameAsSupervisor: sameAsSupervisor,
    timesheetApproverInfo,
    timesheetBackupApproverInfo: {
      firstName: timeBackupApprover.firstName,
      lastName: timeBackupApprover.lastName,
      email: timeBackupApprover.email,
      phoneNumber: timeBackupApprover.phone || undefined,
    },
    timesheetApproverNotApplicable: timesheetApprover?.notApplicable,
  };
};

interface WorkAndBillingLocationRequest {
  workSameAsHomeOrBusinessLocation: boolean;
  workLocation?: LocationModel;
  billingSameAsWorkLocation: boolean;
  billingLocation?: LocationModel;
  billingEmail?: string;
}
// there might be used employee location
export const mapWorkAndBillingLocationToRequest = (
  worksiteLocationValues: WorksiteLocationFormValues,
): WorkAndBillingLocationRequest => {
  const { work, billing } = worksiteLocationValues;
  let workLocation;
  let billingLocation;

  if (!work.locationSameAsMain) {
    workLocation = {
      countryId: toNumber(work.country) || undefined,
      stateId: toNumber(work.state) || undefined,
      internationalStateName: work.internationalStateName || undefined,
      city: work.city || undefined,
      internationalCityName: work.internationalCityName || undefined,
      address: work.address || undefined,
      zipCode: work.zip || undefined,
    };
  }

  if (!billing.locationSameAsWork) {
    billingLocation = {
      countryId: toNumber(billing.country) || undefined,
      stateId: toNumber(billing.state) || undefined,
      internationalStateName: billing.internationalStateName || undefined,
      city: billing.city || undefined,
      internationalCityName: billing.internationalCityName || undefined,
      address: billing.address || undefined,
      zipCode: billing.zip || undefined,
    };
  }

  return {
    workSameAsHomeOrBusinessLocation: work.locationSameAsMain,
    billingSameAsWorkLocation: billing.locationSameAsWork,
    workLocation,
    billingLocation,
  };
};

const getCorrectWorkerWorkType = (
  worker: WorkerFormValues,
  worksiteLocation: WorkSiteWorkLocation | null,
) => {
  const { workerType } = worker;

  if (
    workerType === WorkerTypeEnum.IC ||
    workerType === WorkerTypeEnum.InternationalIC
  ) {
    return worksiteLocation?.country !== countryUSAid
      ? WorkerTypeEnum.InternationalIC
      : WorkerTypeEnum.IC;
  }

  return workerType;
};

export const mapWorkerInformationToRequest = (
  workerInfo: WorkerInfoFormValues,
  assignmentType: AssignmentTypeEnum,
  isDraft: boolean = false,
  worksiteLocation: WorkSiteWorkLocation | null,
): AssignmentWorkerInfoEditModel => {
  const { worker, taxId, workerFileKey } = workerInfo;
  const {
    workerId,
    firstName,
    lastName,
    email,
    phone,
    state,
    city,
    address,
    zip,
    country,
    internationalCityName,
    internationalStateName,
  } = worker;
  const workerType = getCorrectWorkerWorkType(worker, worksiteLocation);
  const isAddWorker = isAddWorkerSelected(workerId);
  let workerInformation: AssignmentWorkerInfoEditModel;

  if (isAddWorker) {
    workerInformation = {
      workerInfo: {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        phoneNumber: phone || undefined,
        taxId:
          assignmentType === AssignmentTypeEnum.ICAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.ICAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      },
      location: {
        stateId: toNumber(state) || undefined,
        city: city || undefined,
        address: address || undefined,
        zipCode: zip || undefined,
        countryId: country || undefined,
        internationalStateName: internationalStateName || undefined,
        internationalCityName: internationalCityName || undefined,
      },
      workerType,
    };
  } else {
    workerInformation = {
      workerId: workerId || undefined,
    };

    // Include workerType only for draft and if worker is not specified
    if (isDraft && !workerId) {
      workerInformation.workerType = workerType;
    }

    if (
      assignmentType === AssignmentTypeEnum.ICAssignment &&
      (!isNil(taxId) || workerFileKey.length)
    ) {
      workerInformation.workerInfo = {
        taxId:
          assignmentType === AssignmentTypeEnum.ICAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.ICAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      };
    }
  }

  return workerInformation;
};

export const mapContractorCompanyInformationToRequest = (
  workerInfo: WorkerInfoFormValues,
  assignmentType: AssignmentTypeEnum,
): AssignmentContractorCompanyInfoEditModel => {
  const { company, taxId, workerFileKey } = workerInfo;
  const {
    companyId,
    companyName,
    firstName,
    lastName,
    email,
    phone,
    state,
    city,
    address,
    zip,
    country,
    internationalCityName,
    internationalStateName,
  } = company;

  const isAddCompany = isAddCompanySelected(companyId);
  let contractorCompanyInfo: AssignmentContractorCompanyInfoEditModel;

  if (isAddCompany) {
    contractorCompanyInfo = {
      companyName: companyName || undefined,
      representativeContact: {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        phoneNumber: phone || undefined,
        taxId:
          assignmentType === AssignmentTypeEnum.SCAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.SCAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      },
      location: {
        countryId: country || undefined,
        stateId: toNumber(state) || undefined,
        internationalStateName: internationalStateName || undefined,
        city: city || undefined,
        internationalCityName: internationalCityName || undefined,
        address: address || undefined,
        zipCode: zip || undefined,
      },
    };
  } else {
    contractorCompanyInfo = {
      companyId: companyId || undefined,
    };

    if (
      assignmentType === AssignmentTypeEnum.SCAssignment &&
      (!isNil(taxId) || workerFileKey.length)
    ) {
      contractorCompanyInfo.representativeContact = {
        taxId:
          assignmentType === AssignmentTypeEnum.SCAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.SCAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      };
    }
  }

  return contractorCompanyInfo;
};

export const mapInternalReportsToRequest = (
  internalReports: AssignmentTeammateSelectModel[],
): AssignmentTeammatesEditModel[] => {
  return internalReports
    .map((internalReport) => {
      return {
        ...internalReport,
        userId:
          internalReport.teammateType === AssignmentTeammateTypeEnum.Teammate
            ? internalReport.userId
            : undefined,
        pendingTeammateEmail:
          internalReport.teammateType ===
          AssignmentTeammateTypeEnum.PendingTeammate
            ? internalReport.userId
            : undefined,
      };
    })
    .filter((internalReport) =>
      internalReport.teammateType === AssignmentTeammateTypeEnum.Teammate &&
      !internalReport.userId
        ? false
        : true,
    );
};

const checkIsDifferentValues = (oldValue: any, newValue: any) =>
  !isEqual(oldValue || '', newValue || '');

export const mapEffectiveDateToInitialValues = (
  previousFinancialInfoValues?: FinancialInfoFormValues,
  currentFinancialInfoValues?: FinancialInfoFormValues,
  predefinedFinancialInfoSchedulerData?: FinancialSchedulerFormValues,
): FinancialSchedulerFormValues | undefined => {
  const financialInfoScheduler: FinancialInfoSchedulerModel = {};
  let multiRatesScheduler: MultiRateFormScheduleValues[] =
    predefinedFinancialInfoSchedulerData?.multiRates || [];

  const hasPayRateChanged = checkIsDifferentValues(
    previousFinancialInfoValues?.payRate,
    currentFinancialInfoValues?.payRate,
  );

  const hasBillRateChanged = checkIsDifferentValues(
    previousFinancialInfoValues?.billRate,
    currentFinancialInfoValues?.billRate,
  );

  if (hasPayRateChanged || hasBillRateChanged) {
    financialInfoScheduler.payRate = !isNil(currentFinancialInfoValues?.payRate)
      ? currentFinancialInfoValues?.payRate
      : undefined;
    financialInfoScheduler.billRate = !isNil(
      currentFinancialInfoValues?.billRate,
    )
      ? currentFinancialInfoValues?.billRate
      : undefined;
    financialInfoScheduler.profitRate = !isNil(
      currentFinancialInfoValues?.profitRate,
    )
      ? currentFinancialInfoValues?.profitRate
      : undefined;
    financialInfoScheduler.mbpRate = !isNil(currentFinancialInfoValues?.mbpRate)
      ? currentFinancialInfoValues?.mbpRate
      : undefined;
  }

  const hasOvertimeChanged = checkIsDifferentValues(
    previousFinancialInfoValues?.overtime,
    currentFinancialInfoValues?.overtime,
  );

  if (hasOvertimeChanged) {
    financialInfoScheduler.overtime = !isNil(
      currentFinancialInfoValues?.overtime,
    )
      ? currentFinancialInfoValues?.overtime
      : undefined;
  }

  const isResetOvertime =
    hasOvertimeChanged &&
    currentFinancialInfoValues?.overtime === OvertimeEnum.Exempt;

  const hasOvertimeBillRateChanged = checkIsDifferentValues(
    !isNil(previousFinancialInfoValues?.overtimeBillRate) &&
      previousFinancialInfoValues?.overtimeBillRate,
    !isNil(currentFinancialInfoValues?.overtimeBillRate) &&
      currentFinancialInfoValues?.overtimeBillRate,
  );

  if (hasOvertimeBillRateChanged) {
    financialInfoScheduler.overtimeBillRate = !isNil(
      currentFinancialInfoValues?.overtimeBillRate,
    )
      ? currentFinancialInfoValues?.overtimeBillRate
      : isResetOvertime
        ? 0
        : undefined;
  }

  const hasOvertimePayRateChanged = checkIsDifferentValues(
    !isNil(previousFinancialInfoValues?.overtimePayRate) &&
      previousFinancialInfoValues?.overtimePayRate,
    !isNil(currentFinancialInfoValues?.overtimePayRate) &&
      currentFinancialInfoValues?.overtimePayRate,
  );

  if (hasOvertimePayRateChanged) {
    financialInfoScheduler.overtimePayRate = !isNil(
      currentFinancialInfoValues?.overtimePayRate,
    )
      ? currentFinancialInfoValues?.overtimePayRate
      : isResetOvertime
        ? 0
        : undefined;
  }

  const hasDTBillRateChanged = checkIsDifferentValues(
    !isNil(previousFinancialInfoValues?.dtBillRate) &&
      previousFinancialInfoValues?.dtBillRate,
    !isNil(currentFinancialInfoValues?.dtBillRate) &&
      currentFinancialInfoValues?.dtBillRate,
  );

  if (hasDTBillRateChanged) {
    financialInfoScheduler.dtBillRate = !isNil(
      currentFinancialInfoValues?.dtBillRate,
    )
      ? currentFinancialInfoValues?.dtBillRate
      : isResetOvertime
        ? 0
        : undefined;
  }

  if (
    checkIsDifferentValues(
      previousFinancialInfoValues?.multiRates,
      currentFinancialInfoValues?.multiRates,
    )
  ) {
    const { oldData: previousMultiRates, newData: currentMultiRates } =
      convertArraysToOneLength<MultiRateFormScheduleValues>(
        previousFinancialInfoValues?.multiRates || [],
        currentFinancialInfoValues?.multiRates || [],
        {
          payTypeId: undefined,
          activePayCodeId: undefined,
          payCodesInfo: [],
        },
      );

    const newScheduler = findMultiRatesDifferences(
      currentMultiRates,
      previousMultiRates,
    );

    const comparisonArray: MultiRateFormScheduleValues[] = [
      ...multiRatesScheduler,
      ...newScheduler,
    ];

    const combinedData: MultiRateFormScheduleValues[] = values(
      reduce(
        comparisonArray,
        (
          accumulator: { [key: string]: MultiRateFormScheduleValues },
          multiRateSchedule,
        ) => {
          const { payTypeId, payCodesInfo, ...rest } = multiRateSchedule;

          if (payTypeId !== undefined) {
            if (!accumulator[payTypeId]) {
              accumulator[payTypeId] = {
                payTypeId,
                payCodesInfo: payCodesInfo || [],
                ...rest,
              };
            } else {
              accumulator[payTypeId].payCodesInfo = concat(
                accumulator[payTypeId].payCodesInfo || [],
                payCodesInfo || [],
              );
            }
          }
          return accumulator;
        },
        {},
      ),
    );

    combinedData.forEach((item: MultiRateFormScheduleValues) => {
      const payCodesInfo = item.payCodesInfo?.map((payCode) => {
        const { specialInstructions, ...otherProperties } = payCode;
        return otherProperties;
      });

      item.payCodesInfo = reduce(
        payCodesInfo,
        (
          accumulator: PayCodeInfoFormScheduleValues[],
          current: PayCodeInfoFormScheduleValues,
        ) => {
          const foundItem = find(accumulator, { payCodeId: current.payCodeId });
          if (foundItem) {
            assign(foundItem, pickBy(current, identity));
          } else {
            accumulator.push(current);
          }
          return accumulator;
        },
        [],
      );
    });

    multiRatesScheduler = combinedData;
  }

  if (!isEmpty(multiRatesScheduler) || !isEmpty(financialInfoScheduler)) {
    return {
      multiRates: multiRatesScheduler,
      financialInfoScheduler: !isEmpty(financialInfoScheduler)
        ? financialInfoScheduler
        : undefined,
      effectiveDate: undefined,
    };
  }
};

const findMultiRatesDifferences = (
  newMultiRates: MultiRateFormValues[],
  oldMultiRates: MultiRateFormValues[],
): MultiRateFormValues[] => {
  let result: MultiRateFormValues[] = [];

  newMultiRates.forEach((newMultiRate) => {
    let newItem: MultiRateFormValues = {
      id: newMultiRate.id,
      payTypeId: newMultiRate.payTypeId,
      activePayCodeId: newMultiRate.activePayCodeId,
      payCodesInfo: [],
    };

    newMultiRate.payCodesInfo
      ?.filter((payCode) => !isNil(payCode.payRate) || !isNil(payCode.billRate))
      .forEach((payCode) => {
        if (!newMultiRate.id) {
          newItem.payCodesInfo?.push(payCode);
        } else {
          const oldMultiRate = oldMultiRates.find(
            (oldMultiRate) => oldMultiRate.id === newMultiRate.id,
          );
          const oldPayCode = oldMultiRate?.payCodesInfo?.find(
            (info) => info.payCodeId === payCode.payCodeId,
          );
          if (oldPayCode) {
            let diff: PayCodeInfoFormValues = {
              payCodeId: payCode.payCodeId,
              hasPayRateCustomLogic: payCode.hasPayRateCustomLogic,
              hasBillRateCustomLogic: payCode.hasBillRateCustomLogic,
            };

            if (payCode.billRate !== oldPayCode.billRate) {
              diff.billRate = payCode.billRate;
              diff.memberBilledIndicator = payCode.memberBilledIndicator;
            }

            if (payCode.payRate !== oldPayCode.payRate) {
              diff.payRate = payCode.payRate;
            }

            if (Object.keys(diff).length >= 4) newItem.payCodesInfo?.push(diff);
          }
        }
      });

    if (newItem.payCodesInfo?.length) result.push(newItem);
  });

  return result;
};

export const mapMultiRates = (
  multiRates?: MultiRateFormValues[],
  isScheduler?: boolean,
): AssignmentMultiRateEditModel[] => {
  return (multiRates || [])
    .flatMap((multiRate) => multiRate?.payCodesInfo || [])
    .map((payCodeInfo) => {
      const obj: AssignmentMultiRateEditModel = {
        payCodeId: payCodeInfo.payCodeId,
        payRate: !isNil(payCodeInfo.payRate) ? payCodeInfo.payRate : undefined,
        billRate: !isNil(payCodeInfo.billRate)
          ? payCodeInfo.billRate
          : undefined,
        hasPayRateCustomLogic: payCodeInfo?.hasPayRateCustomLogic,
        hasBillRateCustomLogic: payCodeInfo?.hasBillRateCustomLogic,
      };

      if (!isScheduler) {
        obj.id = payCodeInfo?.id || undefined;
        obj.specialInstructions = payCodeInfo.specialInstructions || undefined;
        obj.memberBilledIndicator = payCodeInfo.memberBilledIndicator;
      }
      return obj;
    });
};

export const mapFinancialInfoToRequest = (
  values: AssignmentInitialValues,
  financialSchedulerFormValues?: FinancialSchedulerFormValues,
  predefinedInitialValues?: AssignmentInitialValues,
) => {
  const { financialInfo: financialInfoInitialValues } =
    predefinedInitialValues || {};
  const { financialInfo: financialInfoFormValues } = values;

  const filteredInitialMultiRates: MultiRateFormScheduleValues[] = (
    financialInfoInitialValues?.multiRates || []
  ).map((multiRate) => ({
    ...multiRate,
    payCodesInfo: filterPayCodesWithRates(multiRate.payCodesInfo),
  }));

  const multiRatesSchedulerInitialValues: AssignmentMultiRateEditModel[] =
    mapMultiRates(filteredInitialMultiRates);

  const multiRatesScheduler: AssignmentMultiRateEditModel[] = mapMultiRates(
    financialSchedulerFormValues?.multiRates || [],
  );

  const payCodeIdsInScheduler = multiRatesScheduler.map((mr) => mr.payCodeId);
  const multiRatesFormValues: AssignmentMultiRateEditModel[] = mapMultiRates(
    financialInfoFormValues?.multiRates || [],
  ).filter((multiRate) => {
    return (
      payCodeIdsInScheduler.includes(multiRate?.payCodeId) ||
      !isNil(multiRate.billRate) ||
      !isNil(multiRate.payRate) ||
      !isNil(multiRate.specialInstructions)
    );
  });

  const multiRates: AssignmentMultiRateEditModel[] = multiRatesFormValues.map(
    (rate) => {
      const matchingMultiRatesScheduler = multiRatesScheduler.find(
        (multiRateScheduler) => multiRateScheduler.payCodeId === rate.payCodeId,
      );
      const initialMultiRateScheduler = multiRatesSchedulerInitialValues.find(
        (multiRate) => multiRate.payCodeId === rate.payCodeId,
      );

      return {
        id: rate?.id || undefined,
        specialInstructions: rate.specialInstructions?.trim() || undefined,
        memberBilledIndicator: rate.memberBilledIndicator,
        payCodeId: rate.payCodeId,
        payRate: !isNil(matchingMultiRatesScheduler?.payRate)
          ? initialMultiRateScheduler?.payRate
          : rate.payRate,
        billRate: !isNil(matchingMultiRatesScheduler?.billRate)
          ? initialMultiRateScheduler?.billRate
          : rate.billRate,
        hasPayRateCustomLogic: !isNil(
          matchingMultiRatesScheduler?.hasPayRateCustomLogic,
        )
          ? initialMultiRateScheduler?.hasPayRateCustomLogic
          : rate?.hasPayRateCustomLogic || false,
        hasBillRateCustomLogic: !isNil(
          matchingMultiRatesScheduler?.hasBillRateCustomLogic,
        )
          ? initialMultiRateScheduler?.hasBillRateCustomLogic
          : rate?.hasBillRateCustomLogic || false,
      };
    },
  );

  return {
    payRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'payRate',
      )
        ? financialInfoInitialValues?.payRate
        : financialInfoFormValues?.payRate,
    billRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'billRate',
      )
        ? financialInfoInitialValues?.billRate
        : financialInfoFormValues?.billRate,
    dtBillRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'dtBillRate',
      )
        ? financialInfoInitialValues?.dtBillRate
        : financialInfoFormValues?.dtBillRate,
    profitRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'profitRate',
      )
        ? financialInfoInitialValues?.profitRate
        : financialInfoFormValues?.profitRate,
    overtimeBillRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtimeBillRate',
      )
        ? financialInfoInitialValues?.overtimeBillRate
        : financialInfoFormValues?.overtimeBillRate,
    overtimePayRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtimePayRate',
      )
        ? financialInfoInitialValues?.overtimePayRate
        : financialInfoFormValues?.overtimePayRate,

    payType: financialInfoFormValues?.payType,
    overtime:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtime',
      )
        ? financialInfoInitialValues?.overtime
        : financialInfoFormValues?.overtime,
    mbpRate: financialInfoFormValues?.mbpRate,
    dtPayRate: financialInfoFormValues?.dtPayRate,
    multiRates: multiRates,
  };
};

export const mapFinancialInfoSchedulerToRequest = (
  data: FinancialSchedulerFormValues,
): AssignmentFinancialSchedulerEditModel => {
  const values = {
    effectiveDate: !isNil(data.effectiveDate)
      ? getDateWithoutTime(data.effectiveDate)
      : undefined,
    financialInfoScheduler: data.financialInfoScheduler,
    multiRateSchedulers: undefined,
  };

  if (values.financialInfoScheduler?.overtime === OvertimeEnum.Exempt) {
    values.financialInfoScheduler = {
      ...values.financialInfoScheduler,
      overtimeBillRate: undefined,
      overtimePayRate: undefined,
      dtBillRate: undefined,
    };
  }

  const multiRates: MultiRateSchedulerEditModel[] = mapMultiRates(
    data.multiRates || [],
    true,
  );

  return {
    ...values,
    multiRateSchedulers: multiRates.length > 0 ? multiRates : undefined,
  };
};

export const mapMultiRatesSchedulerToInitialValues = (
  multiRates?: MultiRateFormScheduleValues[],
): MultiRateFormScheduleValues[] | undefined => {
  if (!multiRates?.length) return;
  return multiRates.map((multiRate) => {
    const payCodesInfo = filterPayCodesWithRates(multiRate.payCodesInfo);

    return {
      ...multiRate,
      activePayCodeId: payCodesInfo[0]?.payCodeId,
      payCodesInfo,
    };
  });
};

const filterPayCodesWithRates = (
  payCodesInfo: PayCodeInfoFormScheduleValues[] = [],
) => {
  return payCodesInfo
    .filter((payCode) => {
      return (
        !(isNil(payCode.payRate) && isNil(payCode.billRate)) ||
        !isNil(payCode.specialInstructions)
      );
    })
    .map((item) => omitBy(item, isUndefined));
};

export const updateMultiRateSchedulersWithBilledIndicators = (
  multiRates?: AssignmentMultiRateViewModel[],
  multiRateSchedulers?: MultiRateSchedulerViewModel[],
) => {
  const multiRatesMap = new Map(
    multiRates?.map((multiRate) => [multiRate.payCodeId, multiRate]) ?? [],
  );

  const updatedRateSchedules =
    multiRateSchedulers?.map((multiRateSchedule) => {
      const current = multiRatesMap.get(multiRateSchedule.payCodeId);

      return {
        ...multiRateSchedule,
        memberBilledIndicator: current?.memberBilledIndicator ?? undefined,
      };
    }) ?? [];

  return updatedRateSchedules;
};
